import { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';

const CELL_SX = { fontFamily: 'Montserrat', fontSize: '12px' };
const LABEL_SX = { fontFamily: 'Montserrat', fontSize: '13px' };

const DEFAULT_SUBJECT = 'Visa Invitation Ural Federal University, Russia';

const DEFAULT_HTML = `<p>Dear Applicant,</p><p><br></p><p>We are sending you the official study invitation issued by the Ministry of Internal Affairs of the Russian Federation (file attached).</p><p><br></p><p><strong>Your next steps:</strong></p><ol><li>Check the data. Ensure that all personal information in the invitation is correct.</li><li>Clarify visa requirements. Visit the website or contact the Consulate/Embassy of the Russian Federation in the city you specified in your visa application form (in the "Place of visa issuance" field) to learn the current requirements for obtaining a study visa.</li><li>Obtain a study visa. Collect the required set of documents and submit it to your chosen branch of the Consulate/Embassy of the Russian Federation.</li><li><span style="background-color: rgb(102, 185, 102);">Study the guide. Please carefully read the instructions in the attached file.</span></li></ol><p><br></p><p><strong>⚠️ Important deadline information:</strong></p><p>You must arrive at the university and complete your registration within 45 days from the date of entry specified in the invitation. If you fail to meet this deadline, the university may cancel your invitation through the Migration Service.</p><p><br></p><p><strong>Arrival Notification:</strong></p><p>Please inform us of your arrival at least 10 days before your arrival date using the following methods:</p><ul><li>Fill out the online form: <a href="https://urfu.ru/en/international/information-for-prospective-international-students/application-information/arrival-form/" rel="noopener noreferrer" target="_blank">Arrival Notification Form</a></li><li>Email us at: <a href="mailto:adaptation@urfu.ru">adaptation@urfu.ru</a></li></ul><p><br></p><p><strong>Confirmation of receipt:</strong></p><p><span style="background-color: rgb(102, 185, 102);">This email was sent automatically. Please confirm its receipt by sending a reply to <a href="mailto:m.e.kopyrin@urfu.ru">m.e.kopyrin@urfu.ru</a></span></p>`;

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'underline', 'color', 'background', 'list', 'link'];

// students: [{ id, latin_name, country, stayUntil }]
// onSend({ subject, html, useCountryTemplate }) — колбэк из родителя
// onClose — колбэк закрытия
const VisaEmailModal = ({ students = [], onSend, onClose, isSending = false }) => {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [useCountryTemplate, setUseCountryTemplate] = useState(false);

  const handleSend = () => onSend({ subject, html, useCountryTemplate });

  return (
    <Box sx={{ width: 660, display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
      {/* Заголовок — фиксирован */}
      <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '16px', mb: 2, flexShrink: 0 }}>
        Рассылка по визовым приглашениям
      </Typography>

      {/* Прокручиваемая область */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>
        {/* Список студентов */}
        <Typography sx={{ ...LABEL_SX, color: '#555', mb: 0.5 }}>
          Получатели — {students.length} студ.:
        </Typography>
        <Box
          sx={{
            maxHeight: 140,
            overflowY: 'auto',
            mb: 2,
            border: '1px solid #E0E0E0',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>ФИО (лат.)</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>Страна</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>Срок визы (из файла)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s) => (
                <TableRow key={s.id}>
                  <TableCell sx={CELL_SX}>{s.latin_name || '—'}</TableCell>
                  <TableCell sx={CELL_SX}>{s.country || '—'}</TableCell>
                  <TableCell sx={CELL_SX}>
                    {s.stayUntil
                      ? moment(s.stayUntil, 'DD.MM.YYYY', true).isValid()
                        ? s.stayUntil
                        : s.stayUntil
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Чекбокс страны */}
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Checkbox
              checked={useCountryTemplate}
              onChange={(e) => setUseCountryTemplate(e.target.checked)}
              sx={{ color: '#FA7A45', '&.Mui-checked': { color: '#FA7A45' } }}
            />
          }
          label={
            <Typography sx={LABEL_SX}>
              Авто-подбор по приоритету <b>агент → представитель → студент</b>.
              Текст: агенту — RU, представителю — EN, студенту — по стране (КН/ТР/ИСП/РУ, иначе EN).
              Памятка: язык текста + язык студента.
            </Typography>
          }
        />

        {/* Тема */}
        <TextField
          label="Тема письма"
          fullWidth
          size="small"
          value={useCountryTemplate ? 'Определяется автоматически по стране' : subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={useCountryTemplate}
          sx={{ mb: 2 }}
          InputProps={{ style: { fontFamily: 'Montserrat', fontSize: '14px' } }}
          InputLabelProps={{ style: { fontFamily: 'Montserrat' } }}
        />

        {/* Тело письма */}
        {useCountryTemplate ? (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#F5F5F5',
              borderRadius: 1,
              border: '1px solid #E0E0E0',
            }}
          >
            <Typography sx={{ ...LABEL_SX, color: '#757575' }}>
              Тема и текст письма выбираются сервером для каждого студента
              по типу получателя (агент/представитель/студент) и стране.
              Также автоматически прикрепляются нужные памятки.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...LABEL_SX, color: '#555', mb: 0.5 }}>Текст письма:</Typography>
            <Box
              sx={{
                '& .ql-container': { height: '220px', overflowY: 'auto', fontFamily: 'Montserrat' },
                '& .ql-editor p:has(> br:only-child) + ol': { marginTop: '-2em' },
                '& .ql-editor p:has(> br:only-child) + ul': { marginTop: '-2em' },
              }}
            >
              <ReactQuill
                theme="snow"
                value={html}
                onChange={setHtml}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ backgroundColor: '#fff', fontFamily: 'Montserrat' }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Кнопки — фиксированы внизу */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSending}
          sx={{ fontFamily: 'Montserrat', textTransform: 'none' }}
        >
          Отмена
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSend}
          disabled={isSending || students.length === 0}
          sx={{ fontFamily: 'Montserrat', textTransform: 'none' }}
        >
          {isSending ? <CircularProgress size={16} color="inherit" /> : 'Отправить'}
        </Button>
      </Box>
    </Box>
  );
};

export default VisaEmailModal;
